import React, { useState } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Modal, PopoverProps } from "@mui/material";
import EmptyCardPortfolio from "assets/images/EmptyCardPortfolio.png";
import { MaterialReactTable, MaterialReactTableProps, MRT_ColumnDef } from "material-react-table";
import { useAccountSelector, useIsHedgeFundSelect, useOpportunitiesSelect, useReportsTableSelector } from "store";

import { EMPTY_CARD_CONTENT } from "../../../constants/empty-card";
import { useReportMeasuresGroupByOptions } from "../../../hooks";
import { ClientDealTableRow, ReportTableColumn } from "../../../store/types";
import { EmptyCard } from "../../Empty-card/EmptyCard";
import { SelectOption } from "../../forms";
import { TableCell } from "../Measures-table/TableCell";
import { PlaceOnMarketContent } from "./Opportunity/PlaceOnMarketContent";
import { useHedgeFundTable } from "./useHedgeFundTable";
import { useReportsTable } from "./useReportsTable";

export type ColumnSettings = {
  column?: ReportTableColumn;
  isDuplicate: boolean;
} & Pick<PopoverProps, "open" | "anchorEl">;

interface MeasuresTableProps {
  isLoading: boolean;
  title?: string;
  showOriginalCurrency?: boolean;
  isDealView?: boolean;
  refetchReport: () => void;
}

export const tableProps: Partial<MaterialReactTableProps<ClientDealTableRow>> = {
  enablePagination: false,
  enableDensityToggle: false,
  enableStickyHeader: true,
  muiTableContainerProps: {
    sx: { padding: "0 16px 16px 16px" }
  },
  muiTableProps: {
    sx: { borderRadius: "8px", border: "1px solid #DBE8F5" }
  },
  muiTablePaperProps: {
    sx: {
      borderRadius: "8px",
      height: "100%",
      bgcolor: "white",
      boxShadow: "none",
      "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
        "&:first-of-type": {
          overflow: "visible !important"
        }
      },
      " .MuiTableCell-root": {
        borderColor: "gray.50"
      }
    }
  },
  muiTableHeadRowProps: { sx: { backgroundColor: "#F6F8FC", borderRadius: "8px 8px 0 0" } },
  muiTableBodyRowProps: { sx: { backgroundColor: "white" } },
  defaultColumn: {
    muiColumnActionsButtonProps: { sx: { opacity: 0 } },
    muiTableHeadCellProps: {
      sx: {
        " .Mui-TableHeadCell-Content": { flexGrow: 1 },
        " .MuiDivider-root": { borderWidth: 1, opacity: 0 }
      }
    }
  },
  icons: {
    ArrowDownwardIcon: (props: { className: string }) => (
      <div className="measures-table__sort-icons">
        <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
        <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
      </div>
    ),
    DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
  },
  muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
  muiExpandAllButtonProps: { color: "primary" },
  muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } }
};

export const titleColumn = {
  header: "",
  id: "title",
  accessorKey: "title",
  enableColumnDragging: false,
  enableColumnFilter: false,
  enableColumnActions: false,
  enableColumnOrdering: false,
  Cell: ({ cell, row }) => (
    <TableCell
      indentation={row.getParentRow()?.original.deal_id ? 2 : 0}
      dealId={row.original.deal_id}
      value={cell.getValue() as string}
      formatDate
    />
  ),
  AggregatedCell: ({ cell, row }) => (
    <TableCell dealId={row.original.deal_id} value={cell.getValue() as string} aggregated formatDate />
  )
} as MRT_ColumnDef<ClientDealTableRow>;

export const ReportMeasuresTable: React.FC<MeasuresTableProps> = ({ isLoading, refetchReport }) => {
  const { hedge_fund } = useAccountSelector() || {};
  const isHedgeFunds = useIsHedgeFundSelect();
  const measuresTable = useReportsTableSelector();
  const groupByOptions: SelectOption[] = useReportMeasuresGroupByOptions();
  const opportunities = useOpportunitiesSelect();
  const [marketPlaceDealId, setMarketPlaceDealId] = useState<number | null>(null);

  const hedgeFundTable = useHedgeFundTable({ groupByOptions, shouldFetchData: !!hedge_fund && isHedgeFunds });
  const { table: reportsTable, popover } = useReportsTable({ isLoading, groupByOptions, setMarketPlaceDealId });

  const handleClosePlaceOnMarketModal = () => {
    setMarketPlaceDealId(null);
  };

  const table = isHedgeFunds && hedge_fund ? hedgeFundTable : reportsTable;
  const hasRecords = table.getRowCount() > 0;
  const isEmptyResult = !!measuresTable && measuresTable.count_rows === 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        ".MuiTableContainer-root": {
          maxHeight: `calc(100% - ${isHedgeFunds ? "180px" : `${opportunities.length ? "300px" : "180px"}`})`
        }, // 90px - filters static height + 90px more for monthly view
        ...(isHedgeFunds && {
          ".MuiTableCell-root": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          },
          table: {
            "table-layout": "auto"
          },
          ...(hasRecords && {
            thead: {
              borderRadius: "8px 8px 0 0",
              tr: {
                th: {
                  ":first-of-type": { width: "120px", maxWidth: "120px", minWidth: "120px" }
                }
              }
            },
            tbody: {
              tr: {
                td: {
                  ":first-of-type": { width: "120px", maxWidth: "120px", minWidth: "120px" }
                }
              }
            }
          })
        })
      }}
      className="measures-table"
    >
      {isEmptyResult ? (
        <>
          {typeof table.options.renderTopToolbar === "function"
            ? table.options.renderTopToolbar({ table })
            : table.options.renderTopToolbar}
          <EmptyCard
            title={EMPTY_CARD_CONTENT.reportMeasuresTable.title}
            description={{
              text: EMPTY_CARD_CONTENT.reportMeasuresTable.description,
              externalText: EMPTY_CARD_CONTENT.reportMeasuresTable.externalDescription
            }}
            image={{ src: EmptyCardPortfolio }}
          />
        </>
      ) : (
        <MaterialReactTable
          key={(!!measuresTable).toString()} // That recalculates the initial state once the data is loaded
          table={table}
        />
      )}
      {popover}
      <Modal open={Boolean(marketPlaceDealId)} onClose={handleClosePlaceOnMarketModal}>
        <PlaceOnMarketContent
          dealId={marketPlaceDealId}
          onClose={handleClosePlaceOnMarketModal}
          refetchReport={refetchReport}
        />
      </Modal>
    </Box>
  );
};
